import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, matchPath, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { AppShell, StatusDot, token } from '@bp-files/ui';
import { env } from '@/env';
import { APP_COPY } from '@/copy/app.copy';
import { enrollPasskey, platformAuthenticatorAvailable } from '@/lib/passkeyDriver';
import { SETTINGS_INDEX, domainOf } from '@/surfaces/surfaces';
import { SURFACE_COPY } from '@/surfaces/surfaces.copy';
import { SurfaceHeader } from '@/surfaces/SurfaceHeader';
import { SurfaceNav } from '@/components/SurfaceNav';
import { AUDIT_COPY } from '@/components/Audit/audit.copy';
import { AuditSurface } from '@/components/Audit/AuditSurface';
import { BrandMark } from '@/components/BrandMark';
import { SPACE_LIST_COPY } from '@/components/SpaceList/spaceList.copy';
import { DomainRail, DomainTabBar } from '@/components/DomainRail';
import { ExportButton } from '@/components/ExportButton/ExportButton';
import { FolderView } from '@/components/FolderView/FolderView';
import { FolderDestinations } from '@/components/FolderDestinations/FolderDestinations';
import { FOLDER_DESTINATIONS_COPY } from '@/components/FolderDestinations/folderDestinations.copy';
import { FOLDER_COPY } from '@/components/FolderView/folderView.copy';
import { SpaceList } from '@/components/SpaceList/SpaceList';
import { Passkeys } from '@/components/Passkeys/Passkeys';
import { SignatureScreen } from '@/components/Signature/SignatureScreen';
import { ScannersSettings } from '@/components/Settings/ScannersSettings';
import { RecycleBin, RecycleBinButton } from '@/components/RecycleBin/RecycleBin';
import { RECYCLE_BIN_COPY } from '@/components/RecycleBin/recycleBin.copy';
import { GeneralSettings } from '@/components/Settings/GeneralSettings';
import { SettingsIndex } from '@/components/Settings/SettingsIndex';
import { SETTINGS_INDEX_COPY } from '@/components/Settings/settingsIndex.copy';
import { SIGNATURE_COPY } from '@/components/Signature/signature.copy';
import { StorageSurface } from '@/components/Storage/StorageSurface';
import { MembersTab } from '@/components/Users/MembersTab';
import { UsersSurface } from '@/components/Users/UsersSurface';
import { useMe, type MeState } from '@/lib/useMe';
import { useSidebarOpen } from '@/lib/useSidebarOpen';
import { useFolderDestinations } from '@/lib/useFolderDestinations';
import { getMockPersona, setMockPersona, type MockPersona } from '@/lib/mockPersona';
import { getMockPlan, setMockPlan, type MockPlan } from '@/lib/mockPlan';
import { getSession, type SignedInSession } from '@bp-files/client';
import { postAuditExport, type Me } from '@/lib/api';

/**
 * Application root.
 *
 * **All five surfaces are real**, and the chrome that said otherwise is gone:
 * `SurfaceHeader` no longer renders a `SPEC · FIL-1 … FIL-11` eyebrow, and
 * `HealthCard` no longer reports `Storage ok · Mocked true` to whoever opens
 * Files. Requirement ids stayed everywhere their audience is a developer —
 * these comments, commits, PRs — and left the screen.
 *
 * What replaced the mock chip is one banner above every landmark, keyed off the
 * same `VITE_USE_MOCKS` flag. The chip described the build; the banner names the
 * risk, which is somebody putting real evidence into a system that is not
 * holding it.
 *
 * Navigation is by URL (ADR-0007), which is what makes CON-15's space link
 * possible — `/files/spaces/{spaceId}` is an address an agency can copy and send.
 *
 * The caller's role comes from `GET /me`, resolved server-side from the token.
 * It now drives **route access as well as navigation visibility** — but read
 * that carefully, because it is the sentence easiest to misread: SEC-5 puts
 * authorization in the API, enforced per request. `AdminOnly` below decides
 * what a non-admin is *told*, not what they may *do*.
 */
export function App({
  initialMe,
}: {
  /*
    `?: Me | undefined`, not `?: Me`. `exactOptionalPropertyTypes` is on, so the
    short form refuses `Bootstrap`'s one call site, which passes `undefined`
    deliberately — the same shape and the same reason as `AccountMenu`'s `me`
    below. The whole object is defaulted because ~40 tests render `<App />`.
  */
  initialMe?: Me | undefined;
} = {}) {
  /*
    `initialMe` is the sign-in gate's prefetch, handed down by `Bootstrap`
    (ADR-0034). It seeds the hook and skips the first fetch, so the ceremony
    hands over to a loaded app rather than to `SessionGate`'s loading line.
    Absent — the prefetch failed, or a test rendered `App` directly — behaves
    exactly as it always did.
  */
  const { state, retry } = useMe(initialMe);

  const isAdmin = state.kind === 'loaded' && state.me.role === 'admin';
  /*
    Drilled rather than fetched: `useMe` has exactly one call site on purpose.

    **Files does not take it at all**, and has not since the space grid went
    (FIL-1e). It existed for `soleOtherMember` — "the one other agency", a
    question a roster does not have. What is left is the one reader that asks a
    real question: `SpaceList`, which separates leaving from removing (CON-14's
    two verbs share one route and differ only in their target). `RecipientsTab`
    was the other, and its use was the shim this prop outlived (ADR-0072).
  */
  const viewerAgencyId = state.kind === 'loaded' ? state.me.agency.id : '';

  /*
    Read during render rather than held in state, on `MockPersonaSwitch`'s
    reasoning one module over: `lib/session` is not a React store, and this
    value is written once in the bootstrap before `createRoot` and never again.
    Nothing can change it, so nothing needs to re-render when it does.

    `undefined` under `VITE_USE_MOCKS=true`, which is what keeps the whole
    mocked path — and the whole test suite — free of Entra (ADR-0027).
  */
  const session = getSession();
  const [sidebarOpen, setSidebarOpen] = useSidebarOpen();

  return (
    <AppShell
      navLabel={APP_COPY.nav.label}
      navOpen={sidebarOpen}
      onNavOpenChange={setSidebarOpen}
      rail={
        // Withheld until the session resolves, same as `nav` below — a rail
        // that let someone switch domains before we know their role (or
        // whether they are provisioned at all) would be navigation dressed up
        // as chrome.
        state.kind === 'loaded' ? (
          <>
            {/*
              `mb-2` on top of the rail `<nav>`'s own `gap-2`: the mark and the
              first domain icon read as cramped at only 8px, and this is the
              one rail-header gap this product has — nothing else here scales
              with it, so widening it is a local choice rather than a token.

              **A plain `<a>`, not react-router's `Link`.** The sign-in landing
              page (`SignInLanding`) is `Bootstrap`'s territory, rendered
              before `<BrowserRouter>` ever mounts — there is no route for it
              inside this router for a `Link` to reach. An anchor forces a real
              document navigation, which re-runs `main.tsx`'s boot from
              scratch.

              **What that boot lands on changed with ADR-0104**, and for the
              better: the gate finds the same session, has nothing to ask about
              it, and hands straight over — so this is a home link that costs a
              reload rather than a route back to a greeting. Someone signed out
              gets the landing page, because `/me` says so.
            */}
            <a
              href="/"
              aria-label={APP_COPY.rail.brand}
              className="mb-2 block rounded-md px-3 py-2 transition-colors hover:bg-elevated"
            >
              <BrandMark height={18} color="bp-text" />
            </a>
            {/*
              No `isAdmin` — the gear is everybody's now (ADR-0064). It used to
              be hidden from a member because Settings was four admin surfaces
              and nothing else; it opens on General, which is theirs.
            */}
            <DomainRail />
          </>
        ) : undefined
      }
      railLabel={APP_COPY.product}
      /*
        The rail's two domain switches, kept on screen below `sm` where the rail
        is `display:none` — the same gate as `rail` above, withheld until the
        session resolves so nobody switches domains before we know their role.
        `AppShell` shows it only under 640px, the mirror of the rail's `sm:flex`.
      */
      bottomNav={state.kind === 'loaded' ? <DomainTabBar /> : undefined}
      bottomNavLabel={APP_COPY.nav.mobileLabel}
      navToggleLabel={APP_COPY.rail.navToggle}
      /*
        One banner, above everything, whenever the data is not real. It replaced
        a `Running against mocks` chip in the sidebar, which described the build
        rather than the risk — the thing an operator needs to be stopped from
        doing is uploading real evidence into a system that is not holding it.
        Keyed off the same flag; only the volume changed.

        `MockPersonaSwitch` rides along in the same banner rather than its own —
        it is meaningless outside a mocked session, so it never has a reason to
        outlive the sentence warning about one.
      */
      banner={env.VITE_USE_MOCKS ? <EnvironmentBanner onSessionChange={retry} /> : undefined}
      /*
        **Deliberately outside the `state.kind === 'loaded'` check below**, and
        that is the whole point of it. An account that authenticates but has no
        `users` row gets `SessionGate`'s 403 — permanent, and offering no retry,
        because ADR-0004 makes 403 mean "the credential is fine and the holder
        is not a user here". Without a sign-out control in that state, they have
        signed in to a dead end and cannot even try another account.

        No `title` alongside it: `AppShell` gives its `<h1>` to `title` alone,
        and pages have owned their `<h1>` since M7's outline slice. Passing one
        here to "label" the header would put a second `<h1>` on every route.
      */
      headerLeading={
        session === undefined ? undefined : (
          <>
            <HeaderBackButton />
            <HeaderBrand />
          </>
        )
      }
      headerActions={
        session === undefined ? undefined : (
          // `me` only once it has loaded — the menu falls back to the token's
          // address alone, which is the refused-session case above.
          <AccountMenu session={session} me={state.kind === 'loaded' ? state.me : undefined} />
        )
      }
      // No navigation until we know who is asking. Defaulting to "admin" while
      // the session loads would flash surfaces this user may never see.
      //
      // **It used to be withheld from a steered session too, and there is no
      // such thing now** (ADR-0051). A restricted session cannot reach any of
      // these surfaces — `withAuth` refuses every one — so the app used to
      // redirect it to the account screen and hide the rail on the way. It never
      // reaches the app at all now: the sign-in landing page holds it until it
      // has enrolled and asserted, which is what SYS-10 was asking for.
      nav={state.kind === 'loaded' ? <SurfaceNav isAdmin={isAdmin} /> : null}
    >
      {state.kind !== 'loaded' ? (
        <SessionGate state={state} onRetry={retry} />
      ) : (
        <Routes>
          {/* One canonical home. `/` is where a bookmark lands, and it is not a surface. */}
          <Route path="/" element={<Navigate to="/files" replace />} />

          <Route path="/files" element={<FilesSurface isAdmin={isAdmin} />} />

          {/*
            The recycle bin (FIL-11). A sibling of the space list rather than a
            tab above it: Files is a three-level navigation, and it is not
            admin-only — §5.3 gives a member their own deletions to restore.
          */}
          <Route path="/files/recycle-bin" element={<RecycleBin />} />

          {/*
            A shared space's own address (CON-15), which nothing in the app links
            to and somebody may still arrive at from a URL a member agency sent
            them. It renders no screen for anybody: it resolves onto that
            space's root destination (FIL-1e).
          */}
          <Route path="/files/spaces/:spaceId" element={<SpaceRootRedirect />} />

          {/*
            A destination — a folder, or a space's root — and the end of the
            navigation. FIL-2 fixes the structure at two levels, so nothing
            nests below this.
          */}
          <Route
            path="/files/spaces/:spaceId/folders/:folderId"
            element={
              <FolderView
                plan={state.me.agency.plan}
                viewerAgencyId={viewerAgencyId}
                isAdmin={isAdmin}
              />
            }
          />

          {/*
            Spaces (ADR-0032) — first in the Settings domain, and where the
            rail's Settings icon lands. It is the space table's home now, so
            nothing under /files renders one.
          */}
          <Route
            path="/spaces"
            element={
              <AdminOnly isAdmin={isAdmin}>
                <SpacesSurfaceScreen isAdmin={isAdmin} viewerAgencyId={viewerAgencyId} />
              </AdminOnly>
            }
          />

          {/*
            Users is **one surface at one URL** (ADR-0072). It was two tabs on
            two URLs — `/users/members` and `/users/recipients` — with `/users`
            itself a redirect onto the first. The Recipients tab is withdrawn
            with the principal it listed, and a surface with one tab is a
            surface with no tabs (§9), so `/users` is now the route rather than
            a hop to one. Nothing about ADR-0007 changed: navigation is still
            the URL, and there is now one of them.

            The two retired paths need no redirect of their own — the catch-all
            at the bottom of this list takes a stale bookmark to Files, which is
            what it does for every other path this app has stopped serving.
          */}
          <Route
            path="/users"
            element={
              <AdminOnly isAdmin={isAdmin}>
                <UsersSurface>
                  {/*
                    USR-2's domain rule needs the agency's own domain, and this
                    is `me.agency.domain`'s first consumer. A prop rather than a
                    context: the session is already resolved here, and a second
                    `useMe()` would be a second state machine that can disagree
                    with this one.
                  */}
                  <MembersTab
                    agencyDomain={state.me.agency.domain}
                    viewerUserId={state.me.user.id}
                  />
                </UsersSurface>
              </AdminOnly>
            }
          />

          <Route
            path="/storage"
            element={
              <AdminOnly isAdmin={isAdmin}>
                <StorageSurfaceScreen />
              </AdminOnly>
            }
          />
          <Route
            path="/audit"
            element={
              <AdminOnly isAdmin={isAdmin}>
                <AuditSurfaceScreen />
              </AdminOnly>
            }
          />

          {/*
            The domain's index — the settings list itself, for a reader who has
            no sidebar to show it.

            `AppShell` renders the sidebar `hidden … md:block`, so under 768px
            the Settings sub-nav is off the screen. The rail is `hidden …
            sm:flex` and `DomainTabBar` its `sm:hidden` mirror, so the Settings
            *domain* is reachable at every width now — the gear at 640px and up,
            the bottom tab below it — but both land on `SETTINGS_HOME` (General)
            beside a sidebar that is still hidden under 768px. So a reader on a
            phone can switch into Settings and still have no list. The account
            icon carries the link to this index, and this index is that list.

            **Not `SETTINGS_HOME`.** That constant is where the *rail's gear*,
            the *bottom tab*, and the sidebar wordmark land, and it stays
            General: those are pressed by a reader switching domains, and
            sending a domain switch to an index would be a page of links in
            front of the page they were reaching for. This route is for the
            reader who asked for the list.

            No gate. `settingsGroups()` withholds the agency group from a
            member, and each admin route still carries its own `AdminOnly` —
            visibility here, authorization there, and neither is the boundary
            (SEC-5).
          */}
          <Route path="/settings" element={<SettingsIndex isAdmin={isAdmin} />} />

          {/*
            General — where Settings lands, for every role (ADR-0064). The first
            panel in this domain that is not administration: it is about the
            reader's own copy of the app, which is why it needs no gate and why
            it is what made the rail's gear safe to show a member.

            Still not a surface. `surfaces.ts` holds the five things the product
            is *about*; `settingsGroups()` holds what the Settings sidebar lists,
            which is a longer list on purpose.
          */}
          <Route path="/settings/general" element={<GeneralSettings />} />

          {/*
            An account screen rather than a sixth surface (SPEC §9.3). It is not
            in `surfaces.ts`: that registry holds the five things the product is
            *about*, and this one is about the person looking at it.
            `SurfaceNav`'s prefix matching lights nothing outside its own group,
            which is correct — there is no surface to be on.

            **It is in the sidebar now, and it was not** (ADR-0064). That is a
            change to how it is *found*, not to what it does: this was reachable
            only from the account menu, so a person with a passkey to rename
            learned it existed by being told. The menu still spaces it.

            **Management only** (ADR-0051). It was also where a restricted
            session was sent on arrival and where the enrolment prompt pointed;
            both of those are the landing page's now. What is left is USR-11:
            list, rename, remove, and the enrolment that asks the
            shared-workstation question the landing page does not.
          */}
          <Route
            path="/settings/passkeys"
            element={
              <Passkeys enroll={enrollPasskey} platformAvailable={platformAuthenticatorAvailable} />
            }
          />

          {/*
            USR-13's account screen, beside the credentials one and reached the
            same two ways — the account menu and, since ADR-0064, the Settings
            sidebar. `authed` rather than enrollment-tolerant on the API side, so
            a restricted session that typed this path finds the landing page
            holding it rather than a screen it cannot save from.
          */}
          <Route path="/settings/signature" element={<SignatureScreen />} />

          {/*
            USR-16's roster (UPL-13, **ADR-0098**) — read-only, and the screen
            says why. It is gated like the four admin surfaces because a scanner
            is agency infrastructure, and the API answers 403 for a member
            regardless: `adminOnly` here is navigation, never authorization
            (SEC-5).
          */}
          <Route path="/settings/scanners" element={<ScannersSettings />} />

          {/*
            An unknown path is a stale or mistyped space, not an error worth its
            own screen in an MVP with five surfaces.
          */}
          <Route path="*" element={<Navigate to="/files" replace />} />
        </Routes>
      )}
    </AppShell>
  );
}

/**
 * Where `/me` says this session belongs, if it is not where it is.
 *
 * **Two answers, one mechanism**, because they are the same question asked of
 * the same payload on the same load — and two overlapping redirects racing each
 * other on mount is the bug this shape exists to not have.
 *
 * `'account'` is **SYS-10 and is not optional**. A restricted session — signed
 * in with the passcode while already holding a passkey — may reach the account
 * screen and nothing else, and `withAuth` refuses everything else *already*. So
 * without this the app rendered Files and every request under it answered 403:
 * a session the SPA could not render anything for, which reads as a broken
 * product rather than as a step left to finish. There is nothing to defer here,
 * because deferring only buys a screen full of errors.
 *
 * `'prompt'` is **a nudge and is entirely optional**. Somebody holding no
 * credential at all is an ordinary session that the API serves in full; what
 * they get is a page asking them to enrol, with a real "Not now".
 *
 * **The account screen is exempt from both**, or the first would loop and the
 * second would sit on top of the thing it is sending people to.
 */
/**
 * Spaces' Create a space button, styled to match `SpaceList`'s other
 * primary actions even though it no longer lives beside them — same tokens as
 * the accept button's `primaryButtonStyle` there, duplicated rather than
 * imported (this workspace's convention for a one-line style helper with a
 * single caller; `AuditSurface` and `MembersTab` each keep their own for the
 * same reason).
 *
 * It sat on Files until ADR-0032 moved the table it belongs to.
 *
 * The label is `bp-on-accent` (white), not `bp-blue-text` — the readme's own
 * line for solid blue buttons. `bp-blue-text` reads fine on the navy app
 * background it was built for; against this fill it lands under 4:1, short of
 * WCAG AA's 4.5:1 for text this size.
 */
function connectButtonStyle() {
  return {
    borderColor: token('bp-blue'),
    background: token('bp-blue-deep'),
    color: token('bp-on-accent'),
    borderRadius: token('radius-md'),
  };
}

/**
 * Resolve a root-only external space URL (CON-15) onto that space's root.
 *
 * **It always resolves now, and for every role** (FIL-1e). This used to branch
 * on how many folders the space had — one redirected, zero or several went back
 * to `/files` — because a member had nowhere to land when the answer was not
 * exactly one. Every space has a root, so the address a counterpart was handed
 * lands on the room itself, which is the closest thing the product still has to
 * a space root and is a destination rather than a screen.
 *
 * The destination list is still consulted, and only to decide whether the caller
 * may reach the space at all: an unknown or inaccessible id goes back to
 * `/files` rather than to a folder view that would 404.
 */
function SpaceRootRedirect() {
  const { spaceId = '' } = useParams();
  const { state } = useFolderDestinations();

  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {FOLDER_DESTINATIONS_COPY.loading}
      </p>
    );
  }

  if (state.kind === 'error') return <Navigate to="/files" replace />;

  const root = state.folders.find((folder) => folder.spaceId === spaceId && folder.isRoot);
  if (root === undefined) return <Navigate to="/files" replace />;

  return (
    <Navigate
      to={`/files/spaces/${encodeURIComponent(root.spaceId)}/folders/${encodeURIComponent(root.folderId)}`}
      replace
    />
  );
}

/**
 * Files — one list of destinations, for every role (§6, FIL-1d, FIL-1e).
 *
 * **The space table left, and then the space grid did too.** Files is a deputy's
 * workspace, and the work is opening a folder. Connecting, accepting, and
 * severing are acts a deputy cannot perform at all, so they moved to Spaces with
 * the table that carried them (ADR-0032); the shared-space card grid and the
 * space root followed too (ADR-0090). Creating a folder went with the grid at
 * first, naming its space rather than making the admin navigate into one — and
 * has since moved again, into `FolderView`'s own overflow, now that a space's
 * root is a navigated-into destination like any other folder.
 *
 * That leaves one control in the header, so there is no `role="group"` around
 * it any more: `RecycleBinButton` renders as nothing on an empty bin, and a
 * *named* group that is frequently empty is a container a screen reader
 * announces for no one. The bin is every principal's own (§5.3), and so is this
 * surface — `isAdmin` reaches it now only for `EmptyDestinations`' admin-specific
 * empty state, the nudge to go create a space. New folder's visibility gate
 * moved with the act; `FolderView`'s own route threads its own `isAdmin` below.
 */
function FilesSurface({ isAdmin }: { isAdmin: boolean }) {
  const copy = SURFACE_COPY.files;

  return (
    <div className="flex flex-col gap-6">
      {/* No `summary`: Files carries a headline, not a name-plus-sentence pair. */}
      <SurfaceHeader title={copy.title} actionPlacement="hero" actions={<RecycleBinButton />}>
        {/*
          `SurfaceHeader`'s own `gap-5` sets the headline-to-content gap for
          every surface that wraps in it — Audit, Users, Storage included — so
          widening it there would move all five. This `mt-3` is scoped to
          Files' grid alone, taking the gap from 20px to 32px (`space-xl` on
          the design system's own ladder).
        */}
        <div className="mt-3">
          <FolderDestinations isAdmin={isAdmin} />
        </div>
      </SurfaceHeader>

      {/*
        `HealthCard` used to sit here, reporting `Storage ok` and `Mocked true`
        to whoever opened Files. It proved the vertical slice at standup and
        then stayed two milestones past its usefulness — infrastructure state on
        an operational screen reads as an unfinished product, which in a justice
        setting costs trust the system needs. The environment banner in
        `AppShell` carries the one fact an operator has to know instead, and the
        component went with the copy behind it.
      */}
    </div>
  );
}

/**
 * Spaces — the space table, the rooms it lists, and the acts on them
 * (SPEC §4, CON-6, CON-14, CON-22).
 *
 * The table is `SpaceList`: this surface is where it lives, not a rewrite of it.
 * Only one toggle is lifted here — the header button that opens
 * `CreateSpacePanel`. That panel, `AddAgencyPanel`, `MembersPanel`, the fetch
 * and the refresh all stay owned by `SpaceList`; see `SurfaceHeader`'s doc for
 * why. **The button says "Create a folder" now, and that is not a rename.**
 * "Connect an agency" was one act that made a room and put an agency in it;
 * ADR-0071 split it, and the second half is an act on a room that already
 * exists, so it belongs on that room's roster rather than in this header.
 *
 * **`isAdmin` is passed even though the route is already inside `AdminOnly`.**
 * That gate is visibility, not authorization (SEC-5); hard-coding `true` here
 * would bake a claim about permission into a component whose whole contract is
 * that it makes none.
 */
function SpacesSurfaceScreen({
  isAdmin,
  viewerAgencyId,
}: {
  isAdmin: boolean;
  viewerAgencyId: string;
}) {
  const copy = SURFACE_COPY.spaces;
  const [connecting, setConnecting] = useState(false);

  return (
    <SurfaceHeader
      title={copy.title}
      summary={copy.summary}
      actionPlacement="hero"
      actions={
        <button
          type="button"
          onClick={() => {
            setConnecting(true);
          }}
          className="inline-flex min-h-9 shrink-0 items-center justify-center border px-3 py-1.5 text-sm font-medium transition-colors"
          style={connectButtonStyle()}
        >
          {SPACE_LIST_COPY.createSpace.open}
        </button>
      }
    >
      <SpaceList
        isAdmin={isAdmin}
        viewerAgencyId={viewerAgencyId}
        connectOpen={connecting}
        onCloseConnect={() => {
          setConnecting(false);
        }}
      />
    </SurfaceHeader>
  );
}

/**
 * The audit log (§8), inside the same header wrapper `FilesSurface` uses.
 *
 * **The wrapper is `SurfaceHeader` now.** It was `SurfacePlaceholder`, and this
 * comment used to say that M5's Storage slice was the moment to rename it —
 * which is what happened: the last scaffold went, two callers had long since
 * been using it as a plain header, and the third joined them.
 *
 * **EXIT-6's export sits in the header now, beside the title** — it used to sit
 * between the filters and the table, inside `AuditSurface` itself. It reads as a
 * page-level act because it is one: it exports the **whole** log regardless of
 * what the filters below are narrowed to, and a control that lived among the
 * filters looked like it inherited their state even though it never did.
 * `AuditSurface` no longer imports `ExportButton` or `postAuditExport` — this is
 * their only call site now.
 */
function AuditSurfaceScreen() {
  const copy = SURFACE_COPY.audit;

  return (
    <SurfaceHeader
      title={copy.title}
      summary={copy.summary}
      actions={
        <ExportButton kind="audit" label={AUDIT_COPY.export.action} request={postAuditExport} />
      }
    >
      <AuditSurface />
    </SurfaceHeader>
  );
}

/**
 * The Storage surface (§10) — **the last placeholder, now real.**
 *
 * Same wrapper as Files and Audit. Nothing here is admin *authorization*:
 * `AdminOnly` decides what a member is told, and the API refuses every one of
 * those callers on every request regardless (SEC-5). The API also refuses an
 * agency holding no plan, which is STO-5 — a `Receiving` agency has no Storage
 * surface because it has nothing to show — and the surface renders that
 * refusal's own message rather than inventing a second one.
 */
function StorageSurfaceScreen() {
  const copy = SURFACE_COPY.storage;

  return (
    <SurfaceHeader title={copy.title} summary={copy.summary}>
      <StorageSurface />
    </SurfaceHeader>
  );
}

/**
 * The route-aware back control — relocated here from three near-identical
 * `BackLink`s that used to live inside `SpaceRoot`, `FolderView`, and
 * `RecycleBin`. `AppShell` is instantiated once, in this file, wrapping
 * `<Routes>` — its header is not per-route — so a single component driven by
 * the URL replaces three copies that each only knew their own screen.
 *
 * **`matchPath`, not `useParams`.** This renders inside `headerLeading`,
 * which is a *sibling* of `<Routes>` under `<AppShell>` rather than a
 * descendant of any matched `<Route>` — `useParams()` here would always see
 * an empty params object, whatever the URL says. `matchPath` matches a
 * pattern against `location.pathname` directly, with no route-tree
 * dependency, which is exactly what a component outside the tree needs.
 */
function HeaderBackButton() {
  const location = useLocation();
  const { pathname } = location;

  /*
    **One answer for both roles** (FIL-1e). This used to send an admin up to the
    space root and a member back to the destination list. There is no space root
    to go up to, and the list is where both of them came from.

    `/files/spaces/:spaceId` needs no branch of its own: it renders no screen,
    only a redirect onto that space's root, so the back control the reader
    actually sees is the folder one below.
  */
  const folder = matchPath('/files/spaces/:spaceId/folders/:folderId', pathname);
  if (folder) {
    return <BackButtonLink to="/files" label={FOLDER_COPY.backToFiles} />;
  }

  if (pathname === '/files/recycle-bin') {
    return <BackButtonLink to="/files" label={RECYCLE_BIN_COPY.backToSpaces} />;
  }

  /*
    Every settings page but the index itself (`AppShell` renders the sidebar
    `hidden … md:block`, so below that width this header is the only way back
    to it). Un-gated by viewport, on this component's own precedent above —
    the three Files cases render at every width too, and a control that is
    merely redundant beside a visible sidebar is cheaper than one more branch.
  */
  if (pathname !== SETTINGS_INDEX && domainOf(pathname) === 'settings') {
    /*
      `/settings/signature` is the one page here with a second possible
      "back" — the PDF it was opened from to sign (`PdfViewer`'s
      `onNeedsSignature`), carried as router state rather than the URL so a
      stale bookmark to this page can never claim one it was not given.
      `SignatureScreen` already renders its own inline link to the same
      place; this mirrors that destination rather than overriding it; so the
      two can never disagree about where "back" goes, whichever of them a
      reader presses.
    */
    const signedFrom =
      pathname === '/settings/signature'
        ? (location.state as { from?: string } | null)?.from
        : undefined;

    return signedFrom === undefined ? (
      <BackButtonLink to={SETTINGS_INDEX} label={SETTINGS_INDEX_COPY.backToSettings} />
    ) : (
      <BackButtonLink to={signedFrom} label={SIGNATURE_COPY.backToDocument} />
    );
  }

  return null;
}

/**
 * Icon-only, direct-navigate. The label moves to `aria-label` — a screen
 * reader still hears the full sentence, a sighted reader gets a chevron next
 * to the account icon instead of it spelled out on the page below.
 */
function BackButtonLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      aria-label={label}
      className="inline-flex w-fit rounded-md transition-colors hover:text-body"
      style={{ color: token('bp-text-secondary') }}
    >
      <BackIcon />
    </Link>
  );
}

/**
 * The mark, centered in the 56px mobile header, below `sm` only.
 *
 * **`sm:hidden` mirrors the rail's `hidden … sm:flex` exactly**
 * (`AppShell.tsx`), which is the whole point of it: the rail carries this same
 * mark from 640px up, and under 640px the rail is gone and the 56px header is
 * the only chrome there is. One mark is displayed at every width, never two and
 * never none. If either breakpoint moves, they both move.
 *
 * **A plain `<a href="/">`, not a `Link`, and not `to="/files"`** — the reason
 * is written out at the rail's own copy of this above: the sign-in landing page
 * is `Bootstrap`'s territory, rendered before `<BrowserRouter>` mounts, so no
 * route in this router reaches it and only a real document navigation will do.
 *
 * **`absolute`, centered against `AppShell`'s own header, not against
 * `headerLeading`'s `flex items-center gap-2` wrapper it renders inside.**
 * That wrapper sizes to its content, so centering within it would only center
 * the mark within whatever width `HeaderBackButton` happens to leave beside
 * it — present on a folder or space page, absent on `/files`. Taking the mark
 * out of flow and positioning it against the header itself (`relative` there
 * for exactly this) puts it in the same place regardless of what the leading
 * edge is carrying, which is what makes it read as the header's own centered
 * mark rather than a label tacked beside the back chevron.
 */
function HeaderBrand() {
  return (
    <a
      href="/"
      aria-label={APP_COPY.header.brand}
      className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 rounded-md transition-opacity hover:opacity-80 sm:hidden"
    >
      <BrandMark height={18} color="bp-text" />
    </a>
  );
}

/**
 * Lucide's `chevron-left`, hand-copied — the same glyph `SpaceRoot`,
 * `FolderView`, and `RecycleBin` each used to carry their own copy of (no
 * `lucide-react` dependency in this workspace).
 */
function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

/**
 * Lucide's `circle-user`, hand-copied — `apps/web` has no `lucide-react`
 * dependency, and `FolderView`'s `BackIcon` and `MoreActionsIcon` set the
 * precedent. 20px, the middle of the design system's 18–22px range for icons
 * inside controls.
 */
function AccountIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="10" r="3" />
      <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662" />
    </svg>
  );
}

/**
 * Who you are signed in as, and the way out.
 *
 * **A menu now, where this used to argue for two bare controls.** The email and
 * the sign-out button sat inline in the header, which put a full address across
 * the top of every screen and — once `AppShell`'s trailing-edge alignment was
 * fixed — over whatever drawer was open. There is also more to say than there
 * was: the display name and the agency belong here, and four facts inline is a
 * toolbar nobody asked for. One icon, and the identity is a click away.
 *
 * **No `role="menu"`**, deliberately, on `FolderView`'s `RowActions` precedent:
 * that role promises roving-tabindex navigation between items, and this panel is
 * three lines of text and one button. `aria-haspopup="true"` plus
 * `aria-expanded` describes what actually happens. The close mechanism —
 * document `pointerdown` plus Escape, keyed off `open` — is `SpaceList`'s
 * `RowActionsMenu` verbatim in spirit.
 *
 * **`z-10` on the panel is not an oversight.** `AppShell`'s header is `z-20` and
 * `DetailDrawer` is `z-50`: an account menu must never float above a modal.
 *
 * **The trigger carries no border.** It used to sit in a bordered box the
 * same height as `BrandMark`, which read as a second piece of header chrome
 * competing with the wordmark beside it rather than as a single, quiet icon
 * button. `RowActions`' own trigger — colour on hover, no boxed outline at
 * rest — is the precedent now.
 *
 * **The email comes from the session, not from `GET /me`.** This has to render
 * in the states where `/me` failed, which is exactly where it matters most — so
 * `me` is optional here, and a refused session gets an address and a way out
 * with no name or agency beside it (ADR-0004). On the cookie path the session's
 * own address came from `/me` anyway (`bpSession.ts`), which changes nothing
 * here: that path has no token to read one off, and a `/me` that refused never
 * reached the registration at all.
 *
 * Local to this file, beside `EnvironmentBanner` and `SessionGate`, on this
 * file's own rule: a shared component with one caller is an abstraction bought
 * before it is needed. Promote it to `packages/ui` when a second consumer wants
 * a dropdown — it still has none.
 */
function AccountMenu({
  session,
  me,
}: {
  session: SignedInSession;
  /**
   * Required-but-nullable rather than optional: `exactOptionalPropertyTypes` is
   * on, so `me?: Me` would refuse the one call site, which passes `undefined`
   * deliberately. Spelling it this way also makes every caller state which case
   * it is in.
   */
  me: Me | undefined;
}) {
  const [open, setOpen] = useState(false);
  /**
   * Whether a sign-out is in flight.
   *
   * **The button had no state at all, and it read as frozen.** `session.signOut`
   * revokes the session row over the network before it navigates
   * (`lib/bpSession.ts`), which against a cold Function App is seconds of a
   * control giving no sign it was pressed. `SignInLanding` is built on the rule
   * that the button is its own status line and names all of its states; this
   * one, three files away, named none.
   *
   * Nothing resets it, and nothing needs to: the navigation is what ends this
   * component. Disabling also closes the double-click gap for free.
   *
   * Local rather than a widened `SignedInSession.signOut` returning a promise —
   * that seam is shared with `apps/admin` and no caller wants the value. The
   * page always leaves, so "pending until unload" is the whole of the state.
   */
  const [signingOut, setSigningOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={APP_COPY.header.account}
        onClick={() => {
          setOpen((current) => !current);
        }}
        // Borderless, icon-only — `FolderView`'s `RowActions` trigger sets the
        // precedent for a header control that is not the page's primary
        // action: colour on hover, no boxed outline resting on the header at
        // all times.
        className="flex h-9 w-9 items-center justify-center rounded-md text-secondary transition-colors hover:bg-elevated hover:text-body"
      >
        <AccountIcon />
      </button>

      {open ? (
        <div
          className="absolute right-0 z-10 mt-1 flex min-w-max flex-col gap-3 border p-3"
          style={{
            background: token('bp-panel'),
            borderColor: token('bp-border-strong'),
            borderRadius: token('radius-md'),
            boxShadow: token('shadow-md'),
          }}
        >
          {/*
            Not a heading — `App.test.tsx` asserts the banner landmark contains
            none, because `AppShell` gives its `<h1>` to `title` and pages have
            owned theirs since M7's outline slice.
          */}
          <div className="flex flex-col gap-0.5">
            {/*
              `/me`'s name when we have it, the token's when we do not. This
              line used to render nothing in the refused case — a fact we held
              and withheld, on the one screen where identity matters most.
              They agree in every ordinary session; where they differ, the
              directory's own display name is the better one.
            */}
            <span className="text-sm font-medium">{me?.user.name ?? session.displayName}</span>
            <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
              {session.email}
            </span>
            {me === undefined ? null : (
              <span className="text-xs" style={{ color: token('bp-text-muted') }}>
                {me.agency.name}
              </span>
            )}
          </div>

          {/*
            **Secure sign-in and Your signature are still not here, and one link
            to the domain is.**

            Those two were this menu's only way in (SPEC §9.3, USR-13) for two
            milestones, which is exactly why they were here: a person with a
            passkey to rename could find out about the page only by being told.
            ADR-0064 put both in the Settings sidebar — `ACCOUNT_SETTINGS` in
            `surfaces.ts` — and struck the shortcuts on the grounds that two
            named doors to one destination is two places to keep in agreement,
            and that the menu had become a small navigation rather than the
            account's own control.

            **That argument was about pages, and it did not cover the phone.**
            `AppShell` renders the sidebar `hidden … md:block` and the rail
            `hidden … sm:flex`, so under 768px the Settings nav is not on the
            screen, under 640px neither is the gear that reaches it, and this
            icon is the entire chrome. Every settings page was addressable and
            unreachable.

            So: **one link, to the domain, not three to three pages.** It names
            no page, so it cannot disagree with the sidebar about what any page
            is called — `/settings` renders `settingsGroups()`, the same array
            the sidebar renders. This menu is two items now: where everything
            you can change is, and the one act that belongs to no page.
          */}
          <Link
            to={SETTINGS_INDEX}
            onClick={() => {
              setOpen(false);
            }}
            className="border px-3 py-1.5 text-center text-xs font-medium transition-colors hover:bg-elevated"
            style={{
              borderColor: token('bp-border-strong'),
              color: token('bp-text'),
              borderRadius: token('radius-md'),
            }}
          >
            {APP_COPY.header.settings}
          </Link>

          <button
            type="button"
            onClick={() => {
              setSigningOut(true);
              session.signOut();
            }}
            disabled={signingOut}
            aria-busy={signingOut}
            className="border px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70"
            style={{
              borderColor: token('bp-border-strong'),
              color: token('bp-text'),
              borderRadius: token('radius-md'),
            }}
          >
            {signingOut ? APP_COPY.header.signingOut : APP_COPY.header.signOut}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The environment banner's contents — the warning line plus, mock-only, the two
 * switches. Split out because `banner` takes a `ReactNode` and a component (not
 * an inline expression) is what lets each switch hold its own `useState` for
 * the select's current value.
 *
 * The two switches are the two axes a mocked session varies on, and they are
 * genuinely independent: `MockPersonaSwitch` picks *who* is asking (role and
 * grants), `MockPlanSwitch` picks *what their agency bought* (PLN-1). Both end
 * in the same `retry`, so either one is a fresh session load.
 */
function EnvironmentBanner({ onSessionChange }: { onSessionChange: () => void }) {
  return (
    <span className="flex flex-wrap items-center gap-3">
      {APP_COPY.environment.testBanner}
      <MockPersonaSwitch onChange={onSessionChange} />
      <MockPlanSwitch onChange={onSessionChange} />
    </span>
  );
}

/**
 * "View as" — Officer or Agency admin, mock-only.
 *
 * It does not touch `GET /me` itself. It writes `setMockPersona` (read by the
 * `/me` handler in `test/mocks/handlers.ts`) and then calls `retry`, the same
 * function `SessionGate`'s retry button uses, so the switch is exactly a
 * fresh session load under a different mocked identity — every surface,
 * grant, and nav entry follows from the same `useMe()` state machine
 * everything else here does, rather than a second one this component would
 * have to keep in sync.
 *
 * Local state for the select's value, not `getMockPersona()` read on every
 * render: the module is not a React store, so nothing would tell this
 * component to re-render when a different instance changed it. There is only
 * ever one instance, so that gap is theoretical today — but reading it once
 * into `useState` costs nothing and removes the assumption.
 */
function MockPersonaSwitch({ onChange }: { onChange: () => void }) {
  const [persona, setPersona] = useState<MockPersona>(getMockPersona());

  return (
    <label className="flex items-center gap-2 text-xs font-normal">
      {APP_COPY.environment.viewAsLabel}
      <select
        value={persona}
        onChange={(event) => {
          const next = event.target.value as MockPersona;
          setMockPersona(next);
          setPersona(next);
          onChange();
        }}
        className="border px-2 py-1 text-xs"
        style={{
          background: token('bp-input'),
          borderColor: token('bp-border-strong'),
          color: token('bp-text'),
          borderRadius: token('radius-md'),
        }}
      >
        <option value="member">{APP_COPY.environment.personas.member}</option>
        <option value="admin">{APP_COPY.environment.personas.admin}</option>
        {/*
          The third, and the one that changes agency (CON-24). See
          `lib/mockPersona.ts` for what it is defined over and what it is not.
        */}
        <option value="prosecutor">{APP_COPY.environment.personas.prosecutor}</option>
      </select>
    </label>
  );
}

/**
 * "Subscription" — Docs user or Evidence user, mock-only.
 *
 * `MockPersonaSwitch`'s sibling in every respect: it writes a module read by the
 * MSW handlers (`/me` and `/storage` both), then calls the same `retry`, so the
 * whole app reloads under an agency holding a different plan rather than this
 * component patching state in place.
 *
 * **Two values, not three** — PLN-1: Docs is the baseline and Evidence is an
 * add-on that requires it, so there is no Evidence-only agency. An agency
 * holding *no* plan is a different axis again (`agency.state`), and belongs to
 * `MockPersonaSwitch`'s side of the fixture if it is ever wanted.
 */
function MockPlanSwitch({ onChange }: { onChange: () => void }) {
  const [plan, setPlan] = useState<MockPlan>(getMockPlan());

  return (
    <label className="flex items-center gap-2 text-xs font-normal">
      {APP_COPY.environment.subscriptionLabel}
      <select
        value={plan}
        onChange={(event) => {
          const next = event.target.value as MockPlan;
          setMockPlan(next);
          setPlan(next);
          onChange();
        }}
        className="border px-2 py-1 text-xs"
        style={{
          background: token('bp-input'),
          borderColor: token('bp-border-strong'),
          color: token('bp-text'),
          borderRadius: token('radius-md'),
        }}
      >
        <option value="Docs">{APP_COPY.environment.plans.docs}</option>
        <option value="DocsEvidence">{APP_COPY.environment.plans.evidence}</option>
      </select>
    </label>
  );
}

/**
 * The gate on the three admin-only surfaces.
 *
 * **Not a boundary, and it never was** — SEC-5 puts authorization in the API,
 * which refuses these callers on every request whatever renders here. This
 * exists so a member who followed a space is told what happened.
 *
 * A message rather than a redirect: the nav hides these entries, so a member
 * can only arrive deliberately — typed, bookmarked, or sent by a colleague. A
 * silent bounce answers a question they did not ask and reads as a bug.
 *
 * Local rather than its own file, for the same reason as `SessionGate` below:
 * one file, and a shared component with one caller is an abstraction bought
 * before it is needed.
 */
function AdminOnly({ isAdmin, children }: { isAdmin: boolean; children: ReactNode }) {
  if (isAdmin) return children;

  // `role="status"`, not `alert`: nothing failed.
  return (
    <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
      {APP_COPY.status.adminOnly}
    </p>
  );
}

/**
 * The pre-session states.
 *
 * Handled inline rather than as a `packages/ui` composition: it has exactly one
 * consumer, and a shared component with one caller is an abstraction bought
 * before it is needed. Promote it when a second surface wants the treatment.
 */
function SessionGate({
  state,
  onRetry,
}: {
  state: Exclude<MeState, { kind: 'loaded' }>;
  onRetry: () => void;
}) {
  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm text-muted">
        {APP_COPY.session.loading}
      </p>
    );
  }

  // A refusal is permanent: the account authenticated and simply is not set up
  // here. Offering a retry would promise something the button cannot deliver.
  const refused = state.status === 403;

  return (
    <div className="flex flex-col items-start gap-3">
      <p role="alert" className="flex items-center gap-2 text-sm text-danger">
        <StatusDot tone="alert" />
        {refused ? APP_COPY.session.notProvisioned : APP_COPY.session.unavailable}
      </p>

      {refused ? null : (
        <button
          type="button"
          onClick={onRetry}
          className="w-fit border px-3 py-1.5 text-xs font-medium transition-colors"
          style={{
            borderColor: token('bp-border-strong'),
            color: token('bp-text'),
            borderRadius: token('radius-md'),
          }}
        >
          {APP_COPY.session.retry}
        </button>
      )}
    </div>
  );
}
